import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import { getApiUrl, isBackendConfigured } from '../config';

const CustomerAuthContext = createContext();

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  }
  return context;
};

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('customer_token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    setToken(null);
    setCustomer(null);
    localStorage.removeItem('customer_token');
  }, []);

  const fetchCustomer = useCallback(async () => {
    try {
      const response = await axios.get(getApiUrl('/api/customers/me'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setCustomer(response.data);
    } catch (error) {
      console.error('Error fetching customer:', error);
      // Token invalid or expired
      logout();
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    if (token) {
      fetchCustomer();
    } else {
      setLoading(false);
    }
  }, [token, fetchCustomer]);

  const signup = async (signupData) => {
    try {
      const response = await axios.post(getApiUrl('/api/customers/signup'), signupData);
      setToken(response.data.token);
      setCustomer(response.data.customer);
      localStorage.setItem('customer_token', response.data.token);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Signup failed' 
      };
    }
  };

  const login = async (loginData) => {
    try {
      const response = await axios.post(getApiUrl('/api/customers/login'), loginData);
      setToken(response.data.token);
      setCustomer(response.data.customer);
      localStorage.setItem('customer_token', response.data.token);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  return (
    <CustomerAuthContext.Provider value={{
      customer,
      token,
      loading,
      isAuthenticated: !!customer,
      signup,
      login,
      logout
    }}>
      {children}
    </CustomerAuthContext.Provider>
  );
};
